import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs';
import { WidgetAdminHandler } from '@widget-admin/services/widget-admin-handler';
import { QueryBuilderConfigProvider } from '@common/filter/query-builder-config-provider';
import { FormFieldDefinition, FormDefinition  } from '@feature/_app_modules/dynamic-forms/dynamic-form';
import { WidgetConfiguration, WidgetConfigurationsService } from '@feature/widget-configs';
import { mapWherePropertiesMatch } from '@common/index';
import { PmaDocumentCentreConfiguration, SavePmaDocumentCentreWidgetConfigurationRequest } from
  '@feature/pma-document-centre/pma-document-centre-types';
import { WidgetColumnsHttpService } from '@feature/widget-columns/services/widget-columns-http.service';
import { getDefaultWidgetConfigAdminForm, addColumnFieldsToAdminForm, addFilterFieldsToAdminForm } from '@feature/widget-admin/services/widget-admin-form';
import { PmaDocumentCentreWidgetHttpService } from
  '@feature/pma-document-centre/services/pma-document-centre-widget-http.service';
import { AdminPmaDocumentCentreWidgetFilterFieldProvider } from
  '@feature/pma-document-centre/filters/admin-pma-document-centre-widget-filter-field-provider';

@Injectable({
  providedIn: 'root'
})
export class PmaDocumentCentreWidgetAdminHandlerService implements WidgetAdminHandler {

  constructor(
    private readonly widgetColumnsHttp: WidgetColumnsHttpService,
    private readonly widgetConfigService: WidgetConfigurationsService,
    private readonly docCenterHttp: PmaDocumentCentreWidgetHttpService,
  ) { }

  initialise(): boolean | Observable<boolean> {
    return true;
  }

  getAdminForm(selectedConfig: WidgetConfiguration): FormDefinition | Observable<FormDefinition> {
    const getAllColumns = this.widgetColumnsHttp.getAllColumns(selectedConfig.id);
    const getWidgetConfig = this.docCenterHttp.getWidgetConfiguration(selectedConfig.id);

    const all = forkJoin([getAllColumns, getWidgetConfig])
      .pipe(map(([columns, config]) => {

      let formDefinition = getDefaultWidgetConfigAdminForm(selectedConfig);

      formDefinition = addColumnFieldsToAdminForm(selectedConfig, columns, formDefinition);

      const filterFieldProvider = new AdminPmaDocumentCentreWidgetFilterFieldProvider(selectedConfig);
      const queryConfigProvider = new QueryBuilderConfigProvider(filterFieldProvider);
      const queryConfig = queryConfigProvider.getQueryBuilderConfig();

      formDefinition = addFilterFieldsToAdminForm(selectedConfig, queryConfig, formDefinition);

      formDefinition.fields.push({
          key: 'relatedRecordsFilter',
          label: 'Include documents for related records',
          value: config.RelatedRecordsFilter,
          type: 'documents-for-related-records-filter',
          asyncValidation: [],
        } as FormFieldDefinition);

      formDefinition.fields.push({
          key: 'limit',
          label: 'Limit number of documents returned',
          optionLabel: '1,000 item limit',
          value: config.Limit,
          type: 'checkbox',
          asyncValidation: [],
        } as FormFieldDefinition);

      formDefinition.fields.push({
          key: 'categories',
          label: 'Document Categories',
          value: config.SelectedCategories,
          type: 'document-category',
          asyncValidation: [],
        } as FormFieldDefinition);

      return formDefinition;

    }));

    return all;


  }

  createCopyFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue): Observable<boolean> {
    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);
    return this.docCenterHttp.copyWidgetConfiguration(saveRequest);

  }

  saveConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue): Observable<boolean> {
    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);
    return this.docCenterHttp.saveWidgetConfiguration(saveRequest);
  }

  deleteConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    return this.widgetConfigService.delete(copyOfSelectedConfig).pipe(map(() => true));
  }

  private createSaveRequest(selectedConfig: WidgetConfiguration, adminFormValue: any): SavePmaDocumentCentreWidgetConfigurationRequest {

    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);

    const saveRequest = {} as SavePmaDocumentCentreWidgetConfigurationRequest;

    saveRequest.WidgetConfiguration = copyOfSelectedConfig;
    saveRequest.DocumentCentreWidgetConfigurationModel = new PmaDocumentCentreConfiguration();
    saveRequest.DocumentCentreWidgetConfigurationModel.Limit = adminFormValue.limit ? 1000 : 0;
    saveRequest.DocumentCentreWidgetConfigurationModel.RelatedRecordsFilter = adminFormValue.relatedRecordsFilter;
    saveRequest.DocumentCentreWidgetConfigurationModel.SelectedCategories = adminFormValue.categories;

    return saveRequest;
  }


}
